import { inject, Service } from '@angular/core';
import { QueryCashRegisterDtoInput, UpsertCashRegisterDtoInput } from '@h5s/dtos-api';
import {
  BuildCashRegisterCreditNoteGql,
  BuildCashRegisterCreditNoteQueryResult,
  BuildCashRegisterCreditNoteQueryVariables,
  BuildCashRegisterReceiptGql,
  BuildCashRegisterReceiptQueryResult,
  BuildCashRegisterReceiptQueryVariables,
  CashRegister,
  CashRegisterConfigurablePayment,
  CashRegisterCreditNote,
  CashRegisterReceipt,
  CreateCashRegister,
  CreateCashRegisterGql,
  CreateCashRegisterMutationResult,
  CreateCashRegisterMutationVariables,
  DeleteCashRegisterGql,
  DeleteCashRegisterMutationResult,
  DeleteCashRegisterMutationVariables,
  GetCashRegisterConfigurablePaymentsGql,
  GetCashRegisterConfigurablePaymentsQueryResult,
  GetCashRegisterGql,
  GetCashRegisterQueryResult,
  GetCashRegisterQueryVariables,
  GetFiscalClosureParametersGql,
  GetFiscalClosureParametersQueryResult,
  GetFiscalClosureParametersResponse,
  QueryCashRegister,
  ResolveCashRegisterGql,
  ResolveCashRegisterQueryResult,
  ResolveCashRegisterQueryVariables,
  SearchCashRegisterGql,
  SearchCashRegisterQueryResult,
  SearchCashRegisterQueryVariables,
  UpdateCashRegister,
  UpdateCashRegisterGql,
  UpdateCashRegisterMutationResult,
  UpdateCashRegisterMutationVariables,
} from '@h5s/entities';
import { getValueOrError, throwErrorIfNullFromGraphQL } from '@h5s/util-error';
import { mapArrayResponse, mapParamDto, mapQueryParamDto, mapResponse } from '@h5s/util-object';
import { SupportedLanguages } from '@h5s/util-translate';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';

@Service()
export class DataAccessCashRegisterService {
  private readonly apollo = inject(Apollo);

  /**
   * Restituisce un cashRegister tramite id
   */
  public getCashRegisterById(cashRegisterId: number): Observable<CashRegister> {
    const variables: GetCashRegisterQueryVariables = {
      id: cashRegisterId,
    };

    return this.apollo
      .query<GetCashRegisterQueryResult>({
        query: GetCashRegisterGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(CashRegister, result.getCashRegister)),
      );
  }

  /**
   * Restituisce la lista dei cash register
   */
  public searchCashRegister(queryCashRegister: QueryCashRegister): Observable<CashRegister[]> {
    const queryCashRegisterDTO = mapQueryParamDto<QueryCashRegisterDtoInput>(queryCashRegister);

    const variables: SearchCashRegisterQueryVariables = {
      queryCashRegisterDTO,
    };

    return this.apollo
      .query<SearchCashRegisterQueryResult>({
        query: SearchCashRegisterGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(CashRegister, result.searchCashRegister)),
      );
  }

  /**
   * Crea un cash register
   */
  public createCashRegister(createCashRegister: CreateCashRegister): Observable<number> {
    const upsertCashRegisterDTO = mapParamDto<UpsertCashRegisterDtoInput>(createCashRegister);

    const variables: CreateCashRegisterMutationVariables = {
      upsertCashRegisterDTO,
    };

    return this.apollo
      .mutate<CreateCashRegisterMutationResult>({
        mutation: CreateCashRegisterGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => getValueOrError(result.createCashRegister)),
      );
  }

  /**
   * Aggiorna il cashRegister
   */
  public updateCashRegister(updateCashRegister: UpdateCashRegister): Observable<boolean> {
    const upsertCashRegisterDTO = mapParamDto<UpsertCashRegisterDtoInput>(updateCashRegister);

    const variables: UpdateCashRegisterMutationVariables = {
      upsertCashRegisterDTO,
    };

    return this.apollo
      .mutate<UpdateCashRegisterMutationResult>({
        mutation: UpdateCashRegisterGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.updateCashRegister),
      );
  }

  /**
   * Elimina cashRegister
   */
  public deleteCashRegisterById(cashRegisterId: number): Observable<boolean> {
    const variables: DeleteCashRegisterMutationVariables = {
      id: cashRegisterId,
    };

    return this.apollo
      .mutate<DeleteCashRegisterMutationResult>({
        mutation: DeleteCashRegisterGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.deleteCashRegister),
      );
  }

  /**
   * Data un'invoice, restituisce il registratore di cassa sul quale effettuare la stampa.
   *
   * @param invoiceId id della fattura da stampare
   * @param localIpAddress (Attualmente non usato) indirizzo ip della macchina locale
   */
  public resolveCashRegister(invoiceId: number, localIpAddress = 'localhost'): Observable<CashRegister> {
    const variables: ResolveCashRegisterQueryVariables = {
      resolveCashRegisterRequestDTO: {
        invoiceId: invoiceId,
        localIpAddress: localIpAddress,
      },
    };

    return this.apollo
      .query<ResolveCashRegisterQueryResult>({
        query: ResolveCashRegisterGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(CashRegister, result.resolveCashRegister)),
      );
  }

  /**
   * Data un'invoice, costruisce la ricevuta di stampa da passare alla stampante fiscale
   *
   * @param invoiceId id della fattura da stampare
   * @param language lingua in cui stampare la ricevuta fiscale
   */
  public buildCashRegisterReceipt(invoiceId: number, language: SupportedLanguages): Observable<CashRegisterReceipt> {
    const variables: BuildCashRegisterReceiptQueryVariables = {
      buildCashRegisterReceiptRequestDTO: {
        invoiceId: invoiceId,
        language: language,
      },
    };

    return this.apollo
      .query<BuildCashRegisterReceiptQueryResult>({
        query: BuildCashRegisterReceiptGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(CashRegisterReceipt, result.buildCashRegisterReceipt)),
      );
  }

  /**
   * Data un'invoice, ritorna i dati per la stampa della nota di credito
   *
   * @param creditNoteInvoiceId id della fattura da stornare
   */
  public buildCashRegisterCreditNote(creditNoteInvoiceId: number): Observable<CashRegisterCreditNote> {
    const variables: BuildCashRegisterCreditNoteQueryVariables = {
      buildCashRegisterCreditNoteRequestDTO: {
        creditNoteInvoiceId: creditNoteInvoiceId,
      },
    };

    return this.apollo
      .query<BuildCashRegisterCreditNoteQueryResult>({
        query: BuildCashRegisterCreditNoteGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(CashRegisterCreditNote, result.buildCashRegisterCreditNote)),
      );
  }

  /**
   * Restituisce CashRegisterPayment
   */
  public getCashRegisterPayment(): Observable<CashRegisterConfigurablePayment[]> {
    return this.apollo
      .query<GetCashRegisterConfigurablePaymentsQueryResult>({
        query: GetCashRegisterConfigurablePaymentsGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(CashRegisterConfigurablePayment, result.getCashRegisterConfigurablePayments)),
      );
  }

  /**
   * Ritorna i dati necessari per la funzionalità della chiusura fiscale
   *
   * @returns {GetFiscalClosureParametersResponse}
   */
  public getFiscalClosureParameters(): Observable<GetFiscalClosureParametersResponse> {
    return this.apollo
      .query<GetFiscalClosureParametersQueryResult>({
        query: GetFiscalClosureParametersGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(GetFiscalClosureParametersResponse, result.getFiscalClosureParameters)),
      );
  }
}
